import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

export default function Comics() {
  useSeo({
    title: '策驰漫画网漫画库 - 按分类浏览，抢先看最新话与同步更新',
    description:
      '策驰漫画网漫画库支持按热血、恋爱、奇幻、悬疑、校园等分类浏览，抢先看最新话，同步更新进度与汉化进度实时可查。',
    keywords: '策驰漫画网,漫画库,漫画分类,最新话抢先看,同步更新进度,汉化进度',
  });

  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '全部';
  const q = params.get('q') || '';
  const [keyword, setKeyword] = useState(q);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = comics;
    if (category !== '全部') list = list.filter((c) => c.categories.includes(category));
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw)) ||
          c.author.toLowerCase().includes(kw),
      );
    }
    return list;
  }, [category, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setCategory = (c: string) => {
    setParams(c === '全部' ? {} : { category: c });
    setPage(1);
  };

  const onSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = (e.currentTarget.elements.namedItem('kw') as HTMLInputElement).value.trim();
    if (value) setParams({ q: value });
    else setParams(category === '全部' ? {} : { category });
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-20 pt-28 md:px-6 md:pt-32">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '漫画库' }]} />

      <div className="mt-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-heading text-2xl text-foreground-950 md:text-4xl">漫画库</h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
            抢先看热门连载最新话，按分类筛选你喜欢的作品，同步更新进度与汉化进度实时可查。
          </p>
        </div>
        <form
          onSubmit={onSubmitSearch}
          className="flex w-full max-w-xs flex-col gap-2 sm:flex-row sm:rounded-full sm:border sm:border-background-200 sm:bg-background-50 sm:p-1"
        >
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              name="kw"
              type="text"
              autoComplete="off"
              defaultValue={q}
              placeholder="搜索漫画或作者"
              className="w-full rounded-full bg-background-50 px-10 py-2.5 text-sm outline-none ring-primary-300 placeholder:text-foreground-400 focus:ring-2 sm:bg-transparent"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {categories.map((c) => {
          const active = category === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>

      {q && (
        <p className="mt-4 text-sm text-foreground-600">
          关键词 “<strong className="text-primary-600">{q}</strong>” 共找到{' '}
          <strong className="text-foreground-950">{filtered.length}</strong> 部作品
        </p>
      )}

      {paged.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {paged.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      ) : (
        <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
          <i className="ri-book-open-line text-5xl text-foreground-300"></i>
          <p className="mt-4 text-base font-bold text-foreground-800">没有找到相关漫画</p>
          <p className="mt-2 text-sm text-foreground-500">换个关键词或分类试试</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100 disabled:opacity-40"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i + 1)}
              className={`h-9 w-9 rounded-md text-sm transition-colors ${
                currentPage === i + 1
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-100'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage(currentPage + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100 disabled:opacity-40"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}